import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { getImageUrl } from "helpers/Image.helper";
import { challenge as getChallenge, challengeDiaryList } from "api/student/v2/challenge.api";
import { IChallenge, IChallengeDiary } from "api/interfaces/profile.interface";
import { usePopup } from "contexts/PopupContext";
import StudentDiaryDetailPopup from "../../popup/StudentDiaryDetailPopup";

const ChallengeDetail = () => {
    const { challengeId } = useParams<{ challengeId: string }>();
    const { showPopup } = usePopup();
    const [challenge, setChallenge] = useState<IChallenge | null>(null);
    const [challengeDiaries, setChallengeDiaries] = useState<IChallengeDiary[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (challengeId) {
            loadChallengeDetail();
        }
    }, [challengeId]);

    const loadChallengeDetail = async () => {
        try {
            setIsLoading(true);
            
            if (!challengeId) return;
            
            // 챌린지 상세 정보 로드
            const challengeResponse = await getChallenge(challengeId);
            setChallenge(challengeResponse.challenge);
            
            // 챌린지 다이어리 목록 로드
            const diaryResponse = await challengeDiaryList(challengeId);
            setChallengeDiaries(diaryResponse.challengeDiaryList);
        } catch (error) {
            console.error(getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtChallengeLoadError") + ":", error);
            setChallenge(null);
            setChallengeDiaries([]);
        } finally {
            setIsLoading(false);
        }
    };


    const handleEditChallenge = () => {
        // 챌린지 편집 팝업 열기 (추후 구현)
        console.log(getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtChallengeEdit") + ":", challengeId);
    };

    const handleDeleteChallenge = () => {
        if (window.confirm(getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtConfirmDelete"))) {
            // 챌린지 삭제 로직 (추후 구현)
            console.log(getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtChallengeDelete") + ":", challengeId);
        }
    };

    const handleAddDiary = () => {
        // 다이어리 추가 팝업 열기 (추후 구현)
        console.log(getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtDiaryAdd") + ":", challengeId);
    };

    const handleRemoveDiary = (diaryId: number) => {
        if (window.confirm(getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtConfirmRemoveDiary"))) {
            // 다이어리 제거 로직 (추후 구현)
            console.log(getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtDiaryRemove") + ":", diaryId);
        }
    };

    const handleDiaryClick = (diaryId: number) => {
        showPopup(
            <StudentDiaryDetailPopup
                diaryId={diaryId.toString()}
                onClose={() => {}} // PopupContext에서 자동으로 닫힘
            />
        );
    };

    if (isLoading) {
        return (
            <main className="bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
                    <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#CF3688] mx-auto mb-2"></div>
                        <span className="text-gray-500">{getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtLoading")}</span>
                    </div>
                </div>
            </main>
        );
    }

    if (!challenge) {
        return (
            <main className="bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
                    <div className="max-w-3xl mx-auto">
                        <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                            <div className="p-6 sm:p-8 text-center">
                                <div className="text-gray-400 mb-4">
                                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtChallengeNotFound")}
                                </h3>
                                <p className="text-gray-500 mb-6">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtChallengeNotFoundDesc")}
                                </p>
                                <Link
                                    to="/student/growth/challenge"
                                    className="bg-[#CF3688] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#B8327A] transition-colors"
                                >
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtBackToChallenges")}
                                </Link>
                            </div>
                        </section>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
                {/* 뒤로가기 버튼 */}
                <div className="flex items-center mb-6">
                    <Link 
                        to="/student/growth/challenge" 
                        className="inline-flex items-center text-gray-600 hover:text-gray-900"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </Link>
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                    {challenge.title}
                </h2>

                <div className="space-y-6">
                    {/* 챌린지 정보 섹션 */}
                    <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-6 sm:p-8">
                            <div className="flex items-start justify-between mb-6">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                            challenge.isPublic
                                                ? "bg-green-100 text-green-800"
                                                : "bg-gray-100 text-gray-800"
                                        }`}>
                                            {challenge.isPublic
                                                ? getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtPublicChallenge")
                                                : getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtPrivateChallenge")
                                            }
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-6 text-sm text-gray-500 mb-4">
                                        <div className="flex items-center gap-2">
                                            <img
                                                src={
                                                    (challenge.user?.userStudent?.profileImage || challenge.user?.userKoach?.profileImage) 
                                                        ? getImageUrl(
                                                            challenge.user?.userStudent?.profileImage || challenge.user?.userKoach?.profileImage, 
                                                            32, 
                                                            32
                                                        ) 
                                                        : "/imgs/dummy/profile-default.jpg"
                                                }
                                                alt={challenge.user?.userStudent?.nickname || challenge.user?.userKoach?.nickname || 'Unknown'}
                                                className="w-8 h-8 rounded-full object-cover"
                                            />
                                            <span>{challenge.user?.userStudent?.nickname || challenge.user?.userKoach?.nickname || 'Unknown'}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            {formatTimeAgo(challenge.createdAt)}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                                            <div className="text-2xl font-bold text-[#CF3688]">
                                                {challenge.diaryCount || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtDiaryCount")}
                                            </div>
                                        </div>
                                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                                            <div className="text-2xl font-bold text-green-600">
                                                {challenge.participantCount || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtParticipantCount")}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 액션 버튼들 */}
                            <div className="flex gap-2">
                                <button
                                    onClick={handleEditChallenge}
                                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                    title={getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtEditChallenge")}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>

                                <button
                                    onClick={handleDeleteChallenge}
                                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                    title={getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtDeleteChallenge")}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* 챌린지 다이어리 섹션 */}
                    <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-6 sm:p-8">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-semibold text-gray-900">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtChallengeDiaries")}
                                </h3>
                                <button
                                    onClick={handleAddDiary}
                                    className="flex items-center gap-2 px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#B8327A] transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtAddDiary")}
                                </button>
                            </div>

                            {challengeDiaries.length === 0 ? (
                                <div className="text-center py-8">
                                    <div className="text-gray-400 mb-4">
                                        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtNoDiariesInChallenge")}
                                    </h4>
                                    <p className="text-gray-500 mb-6">
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtNoDiariesInChallengeDesc")}
                                    </p>
                                    <button
                                        onClick={handleAddDiary}
                                        className="bg-[#CF3688] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#B8327A] transition-colors"
                                    >
                                        {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtAddDiaryToChallenge")}
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {challengeDiaries.map((challengeDiary) => (
                                        <div key={challengeDiary.id} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <button
                                                        onClick={() => challengeDiary.diary?.id && handleDiaryClick(challengeDiary.diary.id)}
                                                        className="text-lg font-medium text-gray-900 mb-2 hover:text-[#CF3688] transition-colors cursor-pointer text-left"
                                                    >
                                                        {challengeDiary.diary?.title}
                                                    </button>
                                                    <p className="text-gray-600 mb-3 line-clamp-2">
                                                        {challengeDiary.diary?.content}
                                                    </p>
                                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                                        <div className="flex items-center gap-1">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                            {challengeDiary.diary?.viewCount || 0}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                            </svg>
                                                            {challengeDiary.diary?.likeCount || 0}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                            </svg>
                                                            {challengeDiary.diary?.commentCount || 0}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            {formatTimeAgo(challengeDiary.diary?.createdAt || "")}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 ml-4">
                                                    <Link
                                                        to={`/student/growth/diary/${challengeDiary.diary?.id}`}
                                                        className="px-4 py-2 text-[#CF3688] hover:bg-[#CF3688] hover:text-white rounded-lg transition-colors"
                                                    >
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtChallengeView")}
                                                    </Link>
                                                    <button
                                                        onClick={() => handleRemoveDiary(challengeDiary.diaryId)}
                                                        className="px-4 py-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                    >
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "challenge.txtRemoveDiary")}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </main>
    );
};

export default ChallengeDetail;
